import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CalendarIcon, TagIcon } from '@heroicons/react/16/solid';
import {
  HoaReturn,
  WorkOrderReturn,
  WorkOrderUpdate,
} from '../../clients/rust-api-client';
import { formatMoney } from '../../utils/billingHelpers';
import { DatePicker, PropertyPicker } from '../../violation/linear/PropertyPicker';
import { SeverityGlyph } from '../../violation/linear/StatusIcon';
import { Fact, Section } from '../../violation/linear/panelParts';
import {
  formatDay,
  noonUtcMillis,
  utcDay,
} from '../../violation/linear/violationMeta';
import { WorkOrderStatusIcon } from './WorkOrderStatusIcon';
import {
  PRIORITIES,
  STATUSES,
  WORK_ORDER_TYPES,
  isPastScheduled,
  normalizeStatus,
  priorityAsSeverity,
  priorityRank,
  statusLabel,
  titleCase,
} from './workOrderMeta';

/**
 * The work order's right-hand Properties panel, laid out like the
 * violation's (#603). Status, priority, type and the scheduled day are
 * pickers that save on choose; the rest is plain text, changed through
 * "Edit all fields…". Choosing Completed stamps the completion date — the
 * server does that on any move to Completed.
 */
interface Props {
  workOrder: WorkOrderReturn;
  canEdit: boolean;
  /** Field currently saving, so its chip can show it is busy. */
  saving: string | null;
  onUpdate: (patch: WorkOrderUpdate, field: string) => void;
  /** The work order's HOA, once loaded: its name, linking to its page. */
  hoa?: HoaReturn | null;
}

const key = (s: string) => s.toLowerCase().replace(/[_ ]/g, '');

/**
 * Created, completed and updated are moments, not calendar days, so they
 * read in the viewer's own zone — the same day the activity timeline
 * shows. (The scheduled date is a day, and reads by its UTC day.)
 */
const formatMoment = (value: string): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
};

export const WorkOrderPropertiesPanel: React.FC<Props> = ({
  workOrder: w,
  canEdit,
  saving,
  onUpdate,
  hoa,
}) => {
  const readOnly = !canEdit;
  const status = normalizeStatus(w.status);
  const priority = PRIORITIES[priorityRank(w.priority)] ?? titleCase(w.priority);
  const type =
    WORK_ORDER_TYPES.find((t) => key(t) === key(w.work_order_type)) ??
    w.work_order_type;
  const late = isPastScheduled(w);

  return (
    <div className="divide-y divide-line/60">
      <Section title="Properties">
        <PropertyPicker
          options={STATUSES.map((s) => ({
            value: s.value,
            label: s.label,
            icon: <WorkOrderStatusIcon status={s.value} />,
          }))}
          value={status}
          onChange={(value) => value !== status && onUpdate({ status: value }, 'status')}
          placeholder="Change status…"
          ariaLabel={`Status: ${statusLabel(w.status)}${readOnly ? '' : '. Change status'}`}
          readOnly={readOnly}
          busy={saving === 'status'}
          trigger={
            <>
              <WorkOrderStatusIcon status={w.status} />
              {statusLabel(w.status)}
            </>
          }
        />
        <PropertyPicker
          options={PRIORITIES.map((p) => ({
            value: p,
            label: p,
            icon: <SeverityGlyph severity={priorityAsSeverity(p)} />,
          }))}
          value={priority}
          onChange={(value) =>
            value !== priority && onUpdate({ priority: value }, 'priority')
          }
          placeholder="Change priority…"
          ariaLabel={`Priority: ${priority}${readOnly ? '' : '. Change priority'}`}
          readOnly={readOnly}
          busy={saving === 'priority'}
          trigger={
            <>
              <SeverityGlyph severity={priorityAsSeverity(w.priority)} />
              {priority}
            </>
          }
        />
        <PropertyPicker
          options={WORK_ORDER_TYPES.map((t) => ({ value: t, label: titleCase(t) }))}
          value={type}
          onChange={(value) =>
            value !== type && onUpdate({ work_order_type: value }, 'work_order_type')
          }
          placeholder="Change type…"
          ariaLabel={`Type: ${titleCase(type)}${readOnly ? '' : '. Change type'}`}
          readOnly={readOnly}
          busy={saving === 'work_order_type'}
          trigger={
            <>
              <TagIcon className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
              {titleCase(type)}
            </>
          }
        />
        <DatePicker
          value={utcDay(w.scheduled_date)}
          onChange={(day) => {
            const ms = noonUtcMillis(day);
            if (ms !== null) onUpdate({ scheduled_date: ms }, 'scheduled_date');
          }}
          ariaLabel={`Scheduled: ${formatDay(w.scheduled_date) || 'not scheduled'}${readOnly ? '' : '. Change scheduled date'}`}
          readOnly={readOnly}
          busy={saving === 'scheduled_date'}
          trigger={
            <>
              <CalendarIcon
                className={clsx('h-3.5 w-3.5', late ? 'text-red-400' : 'text-zinc-400')}
                aria-hidden
              />
              <span className={clsx(late && 'text-red-400', !w.scheduled_date && 'text-fg-muted')}>
                {w.scheduled_date
                  ? `Scheduled ${formatDay(w.scheduled_date)}`
                  : readOnly
                    ? 'Not scheduled'
                    : 'Schedule'}
              </span>
              {late && <span className="sr-only">(date has passed)</span>}
            </>
          }
        />
      </Section>

      <Section title="Assignment">
        <Fact label="Vendor">{w.vendor_name || '—'}</Fact>
        <Fact label="Assigned to">{w.assigned_to || '—'}</Fact>
      </Section>

      <Section title="Cost">
        <Fact label="Estimated">
          {w.estimated_cost ? formatMoney(w.estimated_cost) : '—'}
        </Fact>
        <Fact label="Actual">
          {w.actual_cost ? formatMoney(w.actual_cost) : '—'}
        </Fact>
      </Section>

      <Section title="Details">
        <Fact label="Association">
          <Link
            to={`/hoa/${w.hoa_id}`}
            className="text-accent-fg hover:underline"
            title={hoa?.name ?? w.hoa_id}
          >
            {hoa?.id === w.hoa_id ? (
              hoa.name
            ) : (
              <span className="font-mono text-[12px]">{w.hoa_id}</span>
            )}
          </Link>
        </Fact>
        {w.home_id && (
          <Fact label="Home">
            <Link
              to={`/hoa/${w.hoa_id}/home/${w.home_id}`}
              className="text-accent-fg hover:underline"
            >
              Open
            </Link>
          </Fact>
        )}
        {w.date_created && <Fact label="Created">{formatMoment(w.date_created)}</Fact>}
        {w.completion_date && (
          <Fact label="Completed">{formatMoment(w.completion_date)}</Fact>
        )}
        {w.date_updated && <Fact label="Updated">{formatMoment(w.date_updated)}</Fact>}
      </Section>
    </div>
  );
};

export default WorkOrderPropertiesPanel;
